import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosRightFill } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import { Link } from '@/components/Link';
import routes from '@/pages/corporate/routes';

import { AdditionalWidgetProps } from '../types';
import { shimmerEffect } from './styles';

interface WidgetContainerProps {
  name?: string;
  widgetId: string;
  children?: React.ReactNode;
  isWidgetEmpty?: boolean;
  className?: string;
  to?: string;
  showSeeAll?: boolean;
  hideBorders?: boolean;
  transparentBackground?: boolean;
  headerClassName?: string;
  seeAllText?: string;
  noHeaderGap?: boolean;
}

function WidgetBox({
  name,
  widgetId,
  children,
  isWidgetEmpty = false,
  hideBorders = false,
  to,
  showSeeAll = true,
  className,
  transparentBackground = false,
  isLoading,
  headerClassName,
  seeAllText,
  noHeaderGap = false,
}: WidgetContainerProps & AdditionalWidgetProps) {
  const { t } = useTranslation();
  const { isMobile } = useScreen();

  return (
    <Stack
      className={cn(
        className,
        'rounded-16 w-full h-full relative',
        'p-0',
        !isMobile && !hideBorders ? 'border border-border' : '',
        transparentBackground ? '!bg-bg-transparent' : 'bg-bg-primary',
      )}
      direction="col"
      gapY={noHeaderGap ? 0 : 18}>
      {name ? (
        <Stack direction="col">
          {name !== undefined ? (
            <Stack
              alignItems="center"
              className={cn('truncate w-full', headerClassName)}
              justifyContent="between">
              <Stack alignItems="center" className="truncate w-full" gapX={12}>
                {name.length ? (
                  <Typography className="truncate" variant={'heading-xs'}>
                    {name}
                  </Typography>
                ) : (
                  <Typography className="truncate text-text-placeholder" variant={'heading-s'}>
                    {t('Common:Untitled')}
                  </Typography>
                )}
              </Stack>

              {isWidgetEmpty || !showSeeAll ? null : (
                <Link className="ml-32" to={to || routes.WidgetDetails(widgetId)}>
                  <Stack direction="row">
                    <Typography color="text-text-secondary" variant="body-short-02">
                      {seeAllText || t('Common:Actions.SeeAll')}
                    </Typography>
                    <ArrowIosRightFill fillColorToken="icon-placeholder" />
                  </Stack>
                </Link>
              )}
            </Stack>
          ) : null}
        </Stack>
      ) : null}
      {isLoading ? <Box className={shimmerEffect} /> : children}
    </Stack>
  );
}

export default WidgetBox;
